import { useCallback } from 'react';
import { useNavigate, NavigateOptions, useLocation } from 'react-router-dom';

/**
 * Navigation options type
 */
export type NavigationOptions = {
  /** Whether the path is relative to the current path */
  relative?: boolean;
  /** Whether to replace the current entry in the history stack */
  replace?: boolean;
};

/**
 * Common paths used in the application
 */
export enum AppPaths {
  // Edit paths
  PROFILE_CARD_EDIT = 'edit/profile-card',
  CONTACT_DETAILS_EDIT = 'edit/contact-details',
  DEMOGRAPHICS_EDIT = 'edit/demographics',
  TESTIMONIALS_EDIT = 'edit/testimonials',
  TESTIMONIALS_ADD = 'add/testimonials',
  TESTIMONIALS_REORDER = 'reorder',
  ROLES_EDIT = 'edit/roles',
  ABOUT_EDIT = 'edit/about',
  EDIT_MEET_THE_TEAM = 'edit/meet-the-team',

  // View paths
  PRIOR_PRODUCTION_VIEW = 'view/prior-production',
  MARKETING_ASSETS_VIEW = 'view/marketing-assets',
  PRODUCTION_OVERVIEW_DETAIL = 'view/deals',
  PROFILE_VIEW = '',
  TEAM_PROFILE_VIEW = '/team',
}

/**
 * Type for the object returned by useNavigation
 */
export type UseNavigationReturn = {
  navigateBack: () => void;
  navigateToPath: (path: string | AppPaths, options?: NavigationOptions) => void;
};

/**
 * Hook that provides navigation functions for the application
 * @returns Object containing navigation utility functions
 */
export const useNavigation = (): UseNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate back to the previous page in history
   * Works correctly with browser history for both own profile and other-user paths
   */
  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /**
   * Navigate to a specific path
   * @param path The path to navigate to
   * @param options Navigation options
   */
  const navigateToPath = useCallback(
    (path: string | AppPaths, options?: NavigationOptions) => {
      const navigateOptions: NavigateOptions = {
        relative: options?.relative ? 'path' : undefined,
        replace: options?.replace,
      };

      // When path is not relative and has other-user in the url, add the base path
      // to the path to navigate correctly
      if (options?.relative === false) {
        const basePath = location.pathname.includes('other-user') ? location.pathname.split('/').slice(0, 3).join('/') : '';
        path = `${basePath}/${path}`;
      }
      navigate(path, navigateOptions);
    },
    [navigate, location?.pathname],
  );

  return {
    navigateBack,
    navigateToPath,
  };
};
